if (!customElements.get('sticky-add-to-cart')) {
  const SMILE_API_HOST = 'platform.smile.io';
  const SMILE_ONLINE_ORDER_TYPE = 'shopify_online_order';
  let smileProgramConfigPromise = null;

  function getSmileRequestHeaders(channelKey) {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Smile-Channel-Key': channelKey,
      'Smile-Client': 'smile-shopify-app-extensions',
      'X-Smile-Language': window.Shopify?.locale || 'en',
    };
  }

  function loadSmileProgramConfig() {
    if (smileProgramConfigPromise) return smileProgramConfigPromise;

    smileProgramConfigPromise = new Promise((resolve) => {
      const attempt = async (retriesLeft) => {
        const channelKey = document.querySelector('.smile-shopify-init')?.dataset?.channelKey;

        if (!channelKey) {
          if (retriesLeft > 0) {
            window.setTimeout(() => attempt(retriesLeft - 1), 500);
            return;
          }

          resolve(null);
          return;
        }

        try {
          const headers = getSmileRequestHeaders(channelKey);
          const programResponse = await fetch(
            `https://${SMILE_API_HOST}/shopify_extensions/init/points_on_product_page?channel_key=${encodeURIComponent(channelKey)}`,
            { headers }
          );

          if (!programResponse.ok) {
            resolve(null);
            return;
          }

          const programData = await programResponse.json();
          const accountId = programData.account?.id;

          if (!accountId) {
            resolve(null);
            return;
          }

          const rulesResponse = await fetch(
            `https://${SMILE_API_HOST}/shopify_extensions/activity_rules?account_id=${accountId}`,
            { headers }
          );

          if (!rulesResponse.ok) {
            resolve(null);
            return;
          }

          const rulesData = await rulesResponse.json();

          resolve({
            programData,
            activityRules: rulesData.activity_rules || [],
          });
        } catch (error) {
          resolve(null);
        }
      };

      attempt(24);
    });

    return smileProgramConfigPromise;
  }

  function getSmileBonusOperand(bonuses, activityRuleId) {
    const bonus = bonuses?.find((entry) => entry.activity_rule_id === activityRuleId);
    return bonus?.bonus_operand ?? 1;
  }

  function calculateSmileEarnablePoints(price, activityRules, bonuses) {
    const onlineOrderRules = activityRules.filter(
      (rule) => rule.type === SMILE_ONLINE_ORDER_TYPE && rule.is_enabled
    );

    if (!onlineOrderRules.length || !price) return 0;

    let lowestVariablePoints = null;
    let lowestFixedPoints = null;

    onlineOrderRules.forEach((rule) => {
      const bonusOperand = getSmileBonusOperand(bonuses, rule.id);

      if (rule.reward_value_type === 'variable') {
        const points = Math.floor(price * rule.multiplier) * bonusOperand;

        if (lowestVariablePoints === null || points < lowestVariablePoints) {
          lowestVariablePoints = points;
        }
      }

      if (rule.reward_value_type === 'fixed') {
        const points = (rule.reward?.value ?? 0) * bonusOperand;

        if (lowestFixedPoints === null || points < lowestFixedPoints) {
          lowestFixedPoints = points;
        }
      }
    });

    return lowestVariablePoints ?? lowestFixedPoints ?? 0;
  }

  function formatSmilePointsLabel(points, singular, plural) {
    const formattedAmount = points.toLocaleString();
    const label = points === 1 ? singular : plural;
    return `${formattedAmount} ${label || 'Points'}`;
  }

  function getSmileVariantPrice(variant) {
    const shopCurrencyPrice = variant.priceAmount / 100;
    const exchangeRate = Number(window.Shopify?.currency?.rate);

    if (exchangeRate && !Number.isNaN(exchangeRate)) {
      return shopCurrencyPrice / exchangeRate;
    }

    return shopCurrencyPrice;
  }

  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.data = this.parseData();
        if (!this.data) return;

        this.overlay = this.querySelector('[data-sticky-atc-overlay]');
        this.panels = {
          options: this.querySelector('[data-sticky-atc-panel="options"]'),
          confirmation: this.querySelector('[data-sticky-atc-panel="confirmation"]'),
        };
        this.trigger = this.querySelector('[data-sticky-atc-trigger]');
        this.spinner = this.querySelector('.sticky-atc__spinner');
        this.priceContainers = Array.from(
          this.querySelectorAll('[data-sticky-atc-price-above], [data-sticky-atc-price-inline]')
        );
        this.payment = this.querySelector('[data-sticky-atc-payment]');
        this.paymentVariantInput = this.querySelector('[data-sticky-atc-variant-input]');
        this.errorTarget = this.querySelector('[data-sticky-atc-error]');
        this.lineItemTarget = this.querySelector('[data-sticky-atc-line-item]');
        this.recommendations = this.querySelector('[data-sticky-atc-recommendations]');
        this.recommendationsContent = this.querySelector('[data-sticky-atc-recommendations-content]');
        this.optionButtons = Array.from(this.querySelectorAll('[data-sticky-atc-option]'));
        this.inlineSizeButtons = Array.from(this.querySelectorAll('[data-sticky-atc-inline-size]'));
        this.pointsContainer = this.querySelector('[data-sticky-atc-points]');
        this.pointsText = this.querySelector('[data-sticky-atc-points-text]');
        this.barErrorTarget = this.querySelector('[data-sticky-atc-bar-error]');
        this.stockWarningTarget = this.querySelector('[data-sticky-atc-stock-warning]');
        this.afterpayWraps = Array.from(this.querySelectorAll('[data-sticky-atc-afterpay-wrap]'));
        this.afterpayWrap = this.afterpayWraps[0] || null;

        this.hasOnlyDefaultVariant = this.dataset.hasOnlyDefaultVariant === 'true';
        this.sizeOptionIndex = Math.max(0, (parseInt(this.dataset.sizeOptionPosition, 10) || 1) - 1);
        this.sizeConfirmed = this.hasExplicitVariantSelection();
        this.activePanel = null;
        this.openedBy = null;

        this.bindEvents();
        this.isInline = this.classList.contains('sticky-atc--inline');
        if (!this.isInline) {
          this.observeLayoutState();
        } else {
          this.setLayout('expanded');
        }
        this.watchPaymentButton();
        this.initWishlistButton();
        this.initRating();
        this.initSmilePoints();
        this.updateBar();
        this.syncSizeConfirmedFromForm();
        this.updatePurchaseState();
        if (!this.isInline) {
          document.body.classList.add('sticky-atc-active');
        }
      }

      hasExplicitVariantSelection() {
        if (this.hasOnlyDefaultVariant) return true;
        return /[?&]variant=\d+/i.test(window.location.search);
      }

      syncSizeConfirmedFromForm() {
        if (this.hasOnlyDefaultVariant) {
          this.sizeConfirmed = true;
          return;
        }

        if (this.sizeConfirmed) return;

        const productInfo = document.getElementById(`MainProduct-${this.dataset.sectionId}`);
        if (!productInfo) return;

        const position = String(this.sizeOptionIndex + 1);
        const selectedSize = productInfo.querySelector(
          `variant-selects input[type="radio"][name$="-${position}"]:checked`
        );

        if (selectedSize) this.sizeConfirmed = true;
      }

      disconnectedCallback() {
        this.stopRatingMirrorPolling();
        this.clearRatingMirror();
        this.wishlistObserver?.disconnect();
        if (this.ratingSyncFrame) window.cancelAnimationFrame(this.ratingSyncFrame);
        this.variantChangeUnsubscriber?.();
        document.removeEventListener('keyup', this.onKeyUp);
        if (this.onLayoutScroll) {
          window.removeEventListener('scroll', this.onLayoutScroll);
          window.removeEventListener('resize', this.onLayoutScroll);
        }
        this.layoutMediaQuery?.removeEventListener('change', this.onLayoutScroll);
        if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
        if (this.layoutFrame) window.cancelAnimationFrame(this.layoutFrame);
        document.body.classList.remove('sticky-atc-active', 'sticky-atc-compact');
      }

      parseData() {
        const script = this.querySelector('[data-sticky-atc-data]');
        if (!script) return null;

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error('sticky-add-to-cart: unable to parse variant data', error);
          return null;
        }
      }

      bindEvents() {
        this.trigger?.addEventListener('click', () => this.onTriggerClick());
        this.overlay?.addEventListener('click', () => this.closeSheet());
        this.querySelectorAll('[data-sticky-atc-close]').forEach((button) => {
          button.addEventListener('click', () => this.closeSheet());
        });
        this.optionButtons.forEach((button) => {
          button.addEventListener('click', () => this.onOptionClick(button));
        });
        this.inlineSizeButtons.forEach((button) => {
          button.addEventListener('click', () => this.onInlineSizeClick(button));
        });
        this.querySelectorAll('[data-sticky-atc-size-guide]').forEach((button) => {
          button.addEventListener('click', () => this.openSizeGuide());
        });
        this.querySelector('[data-sticky-atc-view]')?.addEventListener('click', () => this.openCart());
        this.querySelector('[data-sticky-atc-colours]')?.addEventListener('click', () => this.scrollToColours());

        this.onKeyUp = (event) => {
          if (event.code === 'Escape' && this.activePanel) this.closeSheet();
        };
        document.addEventListener('keyup', this.onKeyUp);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
            this.syncSizeConfirmedFromForm();
            this.updateBar();
            this.updatePurchaseState();
          });
        }
      }

      // Expanded shows sticky-atc__info (title, badges, price above). Compact
      // collapses that block once the bottom of the image gallery scrolls up to
      // the top edge of the sticky bar.
      observeLayoutState() {
        const productRoot = document.getElementById(`MainProduct-${this.dataset.sectionId}`);
        this.mediaWrapper = productRoot?.querySelector('.grid__item.product__media-wrapper');
        this.bar = this.querySelector('.sticky-atc__bar');

        if (!this.mediaWrapper || !this.bar) {
          this.setLayout('expanded');
          return;
        }

        this.layoutMode = 'expanded';
        this.setLayout('expanded');
        this.layoutMediaQuery = window.matchMedia('(max-width: 989px)');

        // The bar height changes between states, so measure it only while
        // expanded and settled, then reuse that value as a fixed trigger line.
        // A live height would flip states mid-transition.
        this.getCompactTriggerLine = () => {
          const settled = Date.now() - (this.lastLayoutChange || 0) > 600;
          if (this.layoutMode === 'expanded' && settled) {
            const height = this.bar.getBoundingClientRect().height;
            if (height > 0) this.expandedBarHeight = height;
          }
          return window.innerHeight - (this.expandedBarHeight || 0);
        };

        this.updateLayoutFromGallery = () => {
          if (!this.layoutMediaQuery.matches) {
            if (this.layoutMode !== 'expanded') {
              this.layoutMode = 'expanded';
              this.setLayout('expanded');
            }
            return;
          }

          const scrollTop =
            window.scrollY ||
            document.documentElement.scrollTop ||
            document.body.scrollTop ||
            0;

          if (scrollTop <= 8) {
            if (this.layoutMode !== 'expanded') {
              this.layoutMode = 'expanded';
              this.setLayout('expanded');
            }
            return;
          }

          const mediaBottom = this.mediaWrapper.getBoundingClientRect().bottom;
          const triggerLine = this.getCompactTriggerLine();
          const hysteresis = 12;
          let nextMode = this.layoutMode;

          if (this.layoutMode === 'expanded' && mediaBottom <= triggerLine - hysteresis) {
            nextMode = 'compact';
          } else if (this.layoutMode === 'compact' && mediaBottom > triggerLine + hysteresis) {
            nextMode = 'expanded';
          }

          if (nextMode === this.layoutMode) return;
          this.layoutMode = nextMode;
          this.setLayout(nextMode);
        };

        this.onLayoutScroll = () => {
          if (this.layoutFrame) return;
          this.layoutFrame = window.requestAnimationFrame(() => {
            this.layoutFrame = null;
            this.updateLayoutFromGallery();
          });
        };

        this.onPageShow = () => {
          const scrollTop =
            window.scrollY ||
            document.documentElement.scrollTop ||
            document.body.scrollTop ||
            0;

          if (scrollTop <= 8) {
            this.layoutMode = 'expanded';
            this.setLayout('expanded');
          }
          this.updateLayoutFromGallery();
        };

        window.requestAnimationFrame(() => {
          // Seed the trigger line before the first state change so a fast
          // initial scroll can't lock in a zero height.
          const height = this.bar.getBoundingClientRect().height;
          if (height > 0) this.expandedBarHeight = height;
          this.updateLayoutFromGallery();
        });

        window.addEventListener('scroll', this.onLayoutScroll, { passive: true });
        window.addEventListener('resize', this.onLayoutScroll);
        window.addEventListener('pageshow', this.onPageShow);
        this.layoutMediaQuery.addEventListener('change', this.onLayoutScroll);
      }

      initWishlistButton() {
        const wishlistTrigger = this.querySelector('[data-sticky-atc-wishlist]');
        const wishlistSwymButton = this.querySelector('.sticky-atc__wishlist-swym');
        if (!wishlistTrigger || !wishlistSwymButton) return;

        wishlistTrigger.addEventListener('click', () => {
          wishlistSwymButton.click();
        });

        const syncWishlistState = () => {
          const mainSwymButton = document.querySelector('.product-form__wishlist-swym');
          const isAdded =
            wishlistSwymButton.classList.contains('swym-added') ||
            Boolean(mainSwymButton?.classList.contains('swym-added'));
          wishlistTrigger.classList.toggle('swym-added', isAdded);
        };

        syncWishlistState();

        this.wishlistObserver = new MutationObserver(syncWishlistState);

        const mainProduct = document.getElementById(`MainProduct-${this.dataset.sectionId}`);
        const mainSwymButton = mainProduct?.querySelector('.product-form__wishlist-swym');

        if (mainSwymButton) {
          this.wishlistObserver.observe(mainSwymButton, {
            attributes: true,
            attributeFilter: ['class'],
          });
        }

        if (wishlistSwymButton) {
          this.wishlistObserver.observe(wishlistSwymButton, {
            attributes: true,
            attributeFilter: ['class'],
          });
        }
      }

      // Shopify only renders the express checkout button when a wallet is
      // available, so let Add fill the bar when nothing comes back.
      watchPaymentButton() {
        if (!this.payment) {
          this.classList.add('sticky-atc--no-payment');
          return;
        }

        const check = () => {
          const rendered = this.payment.querySelector(
            '.shopify-payment-button__button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart'
          );
          this.classList.toggle('sticky-atc--no-payment', !rendered);
          return Boolean(rendered);
        };

        if (check()) return;

        const observer = new MutationObserver(() => {
          if (check()) observer.disconnect();
        });
        observer.observe(this.payment, { childList: true, subtree: true });
        window.setTimeout(() => observer.disconnect(), 10000);
      }

      setLayout(mode) {
        const expanded = mode === 'expanded';
        this.lastLayoutChange = Date.now();

        this.classList.toggle('sticky-atc--expanded', expanded);
        this.classList.toggle('sticky-atc--compact', !expanded);
        document.body.classList.toggle('sticky-atc-compact', !expanded);
        this.updatePurchaseState();
      }

      updatePurchaseState() {
        const needsSizePick = this.classList.contains('sticky-atc--has-sizes');
        const ready = !needsSizePick || this.sizeConfirmed;
        this.classList.toggle('sticky-atc--purchase-ready', ready);
        this.updateAfterpayMessaging();
      }

      get form() {
        return document.getElementById(this.dataset.formId);
      }

      get colourSection() {
        return (
          document.getElementById(`ProductColourSwatches-${this.dataset.sectionId}`) ||
          document.querySelector(
            `#ProductInfo-${this.dataset.sectionId} .product-form__input--colour-swatch`
          ) ||
          document.querySelector(`#ProductInfo-${this.dataset.sectionId} .product-form__input--swatch`)
        );
      }

      scrollToColours() {
        const target = this.colourSection;
        if (!target) return;

        const header =
          document.querySelector('.shopify-section-group-header-group') ||
          document.querySelector('.section-header');
        const headerOffset = header?.getBoundingClientRect().height || 0;
        const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 12;

        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }

      get selectedVariant() {
        const selectedId = this.form?.querySelector('[name="id"]')?.value;
        const variant = this.data.variants.find((item) => String(item.id) === String(selectedId));
        return variant || this.data.variants.find((item) => item.available) || this.data.variants[0];
      }

      variantFor(sizeValue) {
        const selected = this.selectedVariant;

        return this.data.variants.find((variant) =>
          variant.options.every((value, index) => {
            if (index === this.sizeOptionIndex) return value === sizeValue;
            return !selected || value === selected.options[index];
          })
        );
      }

      formatPrice(value) {
        if (!value) return '';
        if (typeof value !== 'string' || !value.includes('<')) return String(value);

        const temp = document.createElement('div');
        temp.innerHTML = value;
        return temp.textContent.trim();
      }

      updateBar() {
        const variant = this.selectedVariant;
        if (!variant) return;

        const isOnSale = variant.compareAtPriceAmount > variant.priceAmount;
        this.priceContainers.forEach((container) => {
          container.classList.toggle('sticky-atc__price--on-sale', isOnSale);

          const current = container.querySelector('[data-sticky-atc-price-current]');
          const compare = container.querySelector('[data-sticky-atc-compare]');

          if (current) current.textContent = this.formatPrice(variant.price);

          if (compare) {
            if (isOnSale) {
              compare.textContent = this.formatPrice(variant.compareAtPrice);
              compare.hidden = false;
            } else {
              compare.textContent = '';
              compare.hidden = true;
            }
          }
        });

        this.syncBadges();
        this.refreshRatingMirror();

        const wishlistSwymButton = this.querySelector('.sticky-atc__wishlist-swym');
        if (wishlistSwymButton) wishlistSwymButton.setAttribute('data-variant-id', variant.id);

        // The theme only syncs its own forms, so the express checkout button
        // needs the selected variant pushed to it.
        this.syncPaymentVariant(variant);

        this.syncInlineSizes();
        this.updateSmilePoints();
        this.updateStockWarning();
        this.updateAfterpayMessaging();
      }

      syncPaymentVariant(variant) {
        if (!this.paymentVariantInput || !variant) return;

        this.paymentVariantInput.value = variant.id;
        this.paymentVariantInput.toggleAttribute('disabled', !variant.available);
        this.paymentVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      formatInstallment(cents) {
        if (cents == null || Number.isNaN(cents)) return '';
        const installment = Math.round(Number(cents) / 4);
        if (window.Shopify?.formatMoney) {
          return window.Shopify.formatMoney(installment);
        }
        return String(installment / 100);
      }

      updateAfterpayMessaging() {
        if (!this.isInline || !this.afterpayWraps?.length) return;

        const needsSize = this.classList.contains('sticky-atc--has-sizes');
        const ready = !needsSize || this.classList.contains('sticky-atc--purchase-ready');

        this.afterpayWraps.forEach((wrap) => {
          wrap.hidden = !ready;
        });

        if (!ready) return;

        const variant = this.selectedVariant;
        if (!variant?.priceAmount) return;

        const nextText = this.formatInstallment(variant.priceAmount);
        this.afterpayWraps.forEach((wrap) => {
          const amountEl = wrap.querySelector('[data-product-afterpay-amount]');
          if (amountEl && amountEl.textContent !== nextText) {
            amountEl.textContent = nextText;
          }
        });
      }

      updateStockWarning() {
        if (!this.stockWarningTarget) return;

        const variant = this.selectedVariant;
        const shouldShowSelection = this.hasOnlyDefaultVariant || this.sizeConfirmed;

        if (!variant || !shouldShowSelection) {
          this.stockWarningTarget.hidden = true;
          this.stockWarningTarget.textContent = '';
          return;
        }

        const message = this.stockWarningMessageFor(variant);

        if (!message) {
          this.stockWarningTarget.hidden = true;
          this.stockWarningTarget.textContent = '';
          return;
        }

        this.stockWarningTarget.textContent = message;
        this.stockWarningTarget.hidden = false;
      }

      stockWarningMessageFor(variant) {
        const threshold = this.data.stockWarning?.inventoryThreshold ?? 10;
        const strings = this.data.stockWarningStrings || {};

        if (variant.inventoryManagement !== 'shopify') return '';
        if (variant.inventoryQuantity <= 0) return '';
        if (variant.inventoryQuantity > threshold) return '';

        const sizeLabel = variant.options[this.sizeOptionIndex] || '';
        const quantity = variant.inventoryQuantity;

        if (quantity <= 3) {
          if (sizeLabel && strings.exactWithSize) {
            return strings.exactWithSize
              .replace('[quantity]', quantity)
              .replace('[option]', sizeLabel);
          }

          return strings.exact?.replace('[quantity]', quantity) || '';
        }

        if (sizeLabel && strings.fewWithSize) {
          return strings.fewWithSize.replace('[option]', sizeLabel);
        }

        return strings.few || '';
      }

      syncInlineSizes() {
        if (!this.inlineSizeButtons.length) return;

        const selected = this.selectedVariant;
        const showSelected = this.sizeConfirmed;

        this.inlineSizeButtons.forEach((button) => {
          const variant = this.variantFor(button.dataset.value);
          const available = Boolean(variant?.available);
          const isSelected =
            showSelected &&
            Boolean(selected) &&
            selected.options[this.sizeOptionIndex] === button.dataset.value;

          button.disabled = !available;
          button.classList.toggle('sticky-atc__size-chip--selected', isSelected);
          button.classList.toggle('sticky-atc__size-chip--unavailable', !available);
          button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });
      }

      initSmilePoints() {
        if (!this.pointsContainer) return;
        this.updateSmilePoints();
      }

      shouldShowSmilePoints(config, variant) {
        const pointsProgram = config.programData.points_program;

        if (!pointsProgram?.is_enabled) return false;

        const excludedProductIds = config.programData.excluded_shopify_product_ids || [];
        if (excludedProductIds.includes(String(this.dataset.productId))) return false;

        const isOnSale = variant.compareAtPriceAmount > variant.priceAmount;
        if (config.programData.exclude_discounted_products && isOnSale) return false;

        return true;
      }

      updateSmilePoints() {
        if (!this.pointsContainer || !this.pointsText) return;

        const hidePoints = () => {
          this.pointsContainer.hidden = true;
          this.pointsText.textContent = '';
        };

        const variant = this.selectedVariant;
        if (!variant?.priceAmount) {
          hidePoints();
          return;
        }

        loadSmileProgramConfig().then((config) => {
          if (!config || !this.shouldShowSmilePoints(config, variant)) {
            hidePoints();
            return;
          }

          const price = getSmileVariantPrice(variant);
          const points = calculateSmileEarnablePoints(
            price,
            config.activityRules,
            config.programData.active_activity_rule_bonuses
          );

          if (!points || points <= 0) {
            hidePoints();
            return;
          }

          const pointsProgram = config.programData.points_program;
          const pointsLabel = formatSmilePointsLabel(
            points,
            pointsProgram.points_label_singular,
            pointsProgram.points_label_plural
          );

          this.pointsText.textContent = `Earn ${pointsLabel}`;
          this.pointsContainer.hidden = false;
        });
      }

      syncBadges() {
        const mainBadges = document.getElementById(`ProductBadges-${this.dataset.sectionId}`);
        const meta = this.querySelector('[data-sticky-atc-meta]');
        if (!meta) return;

        if (!mainBadges) {
          meta.hidden = true;
          meta.textContent = '';
          return;
        }

        const labels = Array.from(
          mainBadges.querySelectorAll(
            '.product-badge, .price__badge-sale, .price__badge-sold-out, .shipping-badge'
          )
        )
          .map((element) => element.textContent.replace(/\s+/g, ' ').trim())
          .filter(Boolean);

        meta.textContent = labels.join(' / ').toUpperCase();
        meta.hidden = labels.length === 0;
      }

      get productInfo() {
        return document.getElementById(`ProductInfo-${this.dataset.sectionId}`);
      }

      get headerTop() {
        return this.productInfo?.querySelector('.product__header-top') ?? null;
      }

      get ratingDestination() {
        return this.querySelector('[data-sticky-atc-rating]');
      }

      get ratingSearchRoot() {
        return this.headerTop || this.productInfo;
      }

      findBazaarvoiceRatingSummary() {
        return this.ratingSearchRoot?.querySelector('[data-bv-show="rating_summary"]') ?? null;
      }

      bazaarvoiceRatingIsReady(bvSummary) {
        if (!bvSummary) return false;

        return Boolean(
          bvSummary.querySelector('.bv_main_container, [class*="bv_main_container"]') ||
            (bvSummary.childElementCount > 0 && bvSummary.textContent.replace(/\s+/g, '').length > 0)
        );
      }

      clearRatingMirror() {
        this.ratingDestination?.querySelector('[data-sticky-atc-rating-mirror]')?.remove();
        this.ratingMirrored = false;
      }

      stopRatingMirrorPolling() {
        if (this.ratingRetryTimer) {
          window.clearInterval(this.ratingRetryTimer);
          this.ratingRetryTimer = null;
        }
      }

      // Never move the live BV node — relocation + observers fight BV hydration and freeze DevTools.
      mirrorRatingToSticky() {
        if (!this.ratingMediaQuery?.matches) return false;

        const destination = this.ratingDestination;
        if (!destination) return false;

        const bvSummary = this.findBazaarvoiceRatingSummary();
        if (!this.bazaarvoiceRatingIsReady(bvSummary)) return false;

        if (this.ratingMirrored && destination.querySelector('[data-sticky-atc-rating-mirror]')) {
          return true;
        }

        const mirror = bvSummary.cloneNode(true);
        mirror.setAttribute('data-sticky-atc-rating-mirror', 'true');
        mirror
          .querySelectorAll(
            '.swym-wishlist-button-bar, .swym-btn-container, .swym-button, .swym-fave-count, [class*="avgRating"], [class*="numReviews"], [class*="reviewCount"], .bv_text'
          )
          .forEach((node) => node.remove());

        destination.querySelector('[data-sticky-atc-rating-mirror]')?.remove();
        destination.querySelector('[data-sticky-atc-rating-fallback]')?.remove();
        destination.appendChild(mirror);

        this.ratingMirrored = true;
        this.stopRatingMirrorPolling();
        return true;
      }

      startRatingMirrorPolling() {
        if (!this.ratingMediaQuery?.matches) return;

        this.stopRatingMirrorPolling();

        if (this.mirrorRatingToSticky()) return;

        let attempts = 0;
        this.ratingRetryTimer = window.setInterval(() => {
          attempts += 1;

          if (this.mirrorRatingToSticky() || attempts >= 24) {
            this.stopRatingMirrorPolling();
          }
        }, 500);
      }

      refreshRatingMirror() {
        if (!this.ratingMediaQuery?.matches) {
          this.clearRatingMirror();
          this.stopRatingMirrorPolling();
          return;
        }

        this.clearRatingMirror();
        this.startRatingMirrorPolling();
      }

      initRating() {
        this.ratingMediaQuery = window.matchMedia('(max-width: 989px)');
        this.ratingMediaQuery.addEventListener('change', () => {
          if (!this.ratingMediaQuery.matches) {
            this.clearRatingMirror();
            this.stopRatingMirrorPolling();
            return;
          }

          this.refreshRatingMirror();
        });

        this.startRatingMirrorPolling();
      }

      onTriggerClick() {
        const variant = this.selectedVariant;
        if (!variant) return;

        if (this.classList.contains('sticky-atc--has-sizes') && !this.sizeConfirmed) {
          this.setError('Please select a size.');
          return;
        }

        if (!variant.available) {
          this.setError('Please select an available size.');
          return;
        }

        this.addToCart(variant);
      }

      onInlineSizeClick(button) {
        const variant = this.variantFor(button.dataset.value);
        if (!variant || !variant.available) return;

        this.sizeConfirmed = true;
        this.setError('');
        this.applyInlineSizeSelection(variant, button.dataset.value);
        this.updatePurchaseState();
        this.updateAfterpayMessaging();
      }

      applyInlineSizeSelection(variant, sizeValue) {
        this.setVariantId(variant.id);
        this.syncMainPicker(sizeValue);
        this.updateBar();
      }

      setVariantId(variantId) {
        const productInfo = document.getElementById(`MainProduct-${this.dataset.sectionId}`);

        productInfo?.updateVariantInputs?.(variantId);
        productInfo?.updateWishlistVariant?.(variantId);

        document
          .querySelectorAll(
            `#${this.dataset.formId} [name="id"], #product-form-installment-${this.dataset.sectionId} [name="id"]`
          )
          .forEach((input) => {
            input.disabled = false;
            if (String(input.value) !== String(variantId)) {
              input.value = variantId;
            }
          });
      }

      openOptions() {
        const selected = this.selectedVariant;

        this.optionButtons.forEach((button) => {
          const variant = this.variantFor(button.dataset.value);
          const note = button.querySelector('[data-sticky-atc-option-note]');
          const available = Boolean(variant?.available);

          button.disabled = !available;
          button.classList.toggle('sticky-atc__option--unavailable', !available);
          button.classList.toggle(
            'sticky-atc__option--selected',
            Boolean(selected) && selected.options[this.sizeOptionIndex] === button.dataset.value
          );

          if (note) note.textContent = this.noteFor(variant);
        });

        this.showPanel('options');
      }

      noteFor(variant) {
        if (!variant) return 'Unavailable';
        if (!variant.available) return 'Out of stock';
        if (variant.inventoryManagement !== 'shopify') return '';
        if (variant.inventoryPolicy === 'continue' && variant.inventoryQuantity <= 0) return 'Preorder';
        if (variant.inventoryQuantity > 0 && variant.inventoryQuantity <= 3) return 'Few items left';
        return '';
      }

      onOptionClick(button) {
        const variant = this.variantFor(button.dataset.value);
        if (!variant || !variant.available) return;

        this.syncMainPicker(button.dataset.value);
        this.addToCart(variant);
      }

      // Keep the on-page variant picker in step with the sheet selection. Inputs
      // for an option are named "<option name>-<option position>".
      syncMainPicker(value) {
        const productInfo = document.getElementById(`MainProduct-${this.dataset.sectionId}`);
        if (!productInfo) return;

        const position = String(this.sizeOptionIndex + 1);
        const radio = Array.from(
          productInfo.querySelectorAll('variant-selects input[type="radio"], input[type="radio"][form]')
        ).find((input) => input.name.trim().endsWith(`-${position}`) && input.value === value);

        if (radio) {
          if (!radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        const select = productInfo.querySelector(`#Option-${this.dataset.sectionId}-${this.sizeOptionIndex}`);

        if (select && select.value !== value) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      addToCart(variant) {
        this.setLoading(true);
        this.setError('');

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = {
          items: [{ id: variant.id, quantity: 1 }],
        };

        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.setError(response.description || response.message);
              return;
            }

            this.updateCartSections(cart, response);

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'sticky-add-to-cart',
                productVariantId: variant.id,
                cartData: response,
              });
            }

            this.showConfirmation(variant);
            this.loadRecommendations();
          })
          .catch((error) => {
            console.error(error);
            this.setError(window.cartStrings?.error || 'There was an error adding this item to your basket.');
          })
          .finally(() => this.setLoading(false));
      }

      updateCartSections(cart, response) {
        if (!cart || !response.sections) return;

        cart.getSectionsToRender().forEach((section) => {
          const element = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          const html = response.sections[section.id];

          if (element && html) element.innerHTML = cart.getSectionInnerHTML(html, section.selector);
        });

        cart.classList.remove('is-empty');
        cart.querySelector('.drawer__inner')?.classList.remove('is-empty');
      }

      showConfirmation(variant) {
        const productTitle = this.dataset.productTitle || '';
        const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
        const image = variant.image
          ? `<img class="sticky-atc__line-item-image" src="${encodeURI(
              variant.image
            )}" alt="" width="90" height="120" loading="lazy">`
          : '';

        this.lineItemTarget.innerHTML = `
          ${image}
          <div class="sticky-atc__line-item-details">
            <p class="sticky-atc__line-item-title">${this.escape(productTitle)}</p>
            ${variantTitle ? `<p class="sticky-atc__line-item-variant">${this.escape(variantTitle)}</p>` : ''}
            <p class="sticky-atc__line-item-price">${this.escape(this.formatPrice(variant.price))}</p>
          </div>
        `;

        this.showPanel('confirmation');
      }

      escape(value) {
        const element = document.createElement('span');
        element.textContent = value ?? '';
        return element.innerHTML;
      }

      loadRecommendations() {
        if (!this.recommendations || this.recommendationsLoaded) return;
        this.recommendationsLoaded = true;

        const url = `${this.dataset.recommendationsUrl}?section_id=sticky-atc-recommendations&product_id=${this.dataset.productId}&limit=8`;

        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            const list = html.querySelector('[data-sticky-atc-recommendations-list]');

            if (!list || !list.children.length) return;

            this.recommendationsContent.innerHTML = list.outerHTML;
            this.recommendations.hidden = false;
          })
          .catch(() => {
            this.recommendationsLoaded = false;
          });
      }

      showPanel(name) {
        Object.entries(this.panels).forEach(([key, panel]) => {
          if (panel) panel.hidden = key !== name;
        });

        this.overlay.hidden = false;
        if (!this.activePanel) this.openedBy = document.activeElement;
        this.activePanel = name;
        document.body.classList.add('overflow-hidden');

        // Force a reflow so the panel transitions in from its hidden position.
        void this.panels[name].offsetHeight;
        this.classList.add('sticky-atc--sheet-open');

        if (typeof trapFocus === 'function') {
          trapFocus(this.panels[name], this.panels[name].querySelector('button'));
        }
      }

      closeSheet() {
        if (!this.activePanel) return;

        this.classList.remove('sticky-atc--sheet-open');
        this.activePanel = null;
        document.body.classList.remove('overflow-hidden');

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.openedBy);
        }

        window.setTimeout(() => {
          if (this.activePanel) return;
          Object.values(this.panels).forEach((panel) => {
            if (panel) panel.hidden = true;
          });
          this.overlay.hidden = true;
        }, 300);
      }

      openCart() {
        const cart = document.querySelector('cart-drawer');
        this.closeSheet();

        if (cart) {
          window.setTimeout(() => cart.open(), 150);
          return;
        }

        window.location.href = window.routes.cart_url;
      }

      // The size chart modal is lifted above the sheet in CSS, so the size
      // selection stays open behind it.
      openSizeGuide() {
        const popup = document.querySelector('#size-chart-popup');
        if (popup) {
          popup.classList.remove('hidden');
          return;
        }

        document.querySelector('.size-chart-header')?.click();
      }

      setLoading(loading) {
        this.trigger?.classList.toggle('sticky-atc__button--loading', loading);
        this.optionButtons.forEach((button) => button.classList.toggle('sticky-atc__option--loading', loading));
        this.inlineSizeButtons.forEach((button) => {
          button.classList.toggle('sticky-atc__size-chip--loading', loading);
        });
        if (this.spinner) this.spinner.hidden = !loading;
      }

      setError(message) {
        const targets = [this.barErrorTarget, this.errorTarget].filter(Boolean);
        if (!targets.length) return;

        targets.forEach((target) => {
          target.textContent = message || '';
          target.hidden = !message;
        });
      }
    }
  );
}
